/**
 * Session store — the merchant identity (`x-merchant`) and the Fastrr dashboard
 * session token (`x-auth-token`) the admin API requires.
 *
 * In production the host (Fastrr dashboard / Shopify admin) supplies both via
 * the iframe URL (`?merchant=`, `?token=`). For standalone use there is a
 * Connect screen. Values are persisted to sessionStorage and pushed into the
 * API request context so the client can attach them to every call.
 */
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  setActiveAuthToken,
  setActiveMerchantDomain,
  setActiveSessionInfo,
  setAuthExpiredHandler,
} from '@/api/requestContext';
import { fetchSessionInfo } from '@/api/session';
import { environment } from '@/config/env';

/** Where the merchant is sent after logout, or after the session becomes invalid. */
export const LOGOUT_REDIRECT_URL = 'https://checkout-dashboard.shiprocket.in/dashboard';

interface SessionState {
  merchantDomain: string;
  authToken: string;
  /** Host-supplied session info, forwarded as `x-session-info` on every call. */
  sessionInfo: string;
  /** Set true when an API call returns 401/403 so the shell can re-prompt. */
  authExpired: boolean;
  /**
   * False until `bootstrap()` has resolved a fresh session info. The shell must
   * not render (or fire any data calls) until this is true — otherwise the first
   * requests would go out with the stale, rehydrated session info.
   */
  isReady: boolean;
  /**
   * True from the moment `disconnect()` is called until the tab actually
   * navigates away. Lets the shell show a blank/loading state instead of
   * flashing the Connect screen while the redirect is still in flight.
   */
  isLoggingOut: boolean;
  isConnected: () => boolean;
  /** Resolve merchant/token from the URL or env on boot, then fetch session info. */
  bootstrap: () => Promise<void>;
  connect: (merchantDomain: string, authToken: string, sessionInfo?: string) => Promise<void>;
  disconnect: () => void;
  /** Clear the session and send the tab to {@link LOGOUT_REDIRECT_URL}. */
  redirectToLogout: () => void;
  flagAuthExpired: () => void;
}

/**
 * Use an explicit session info handle when the host already supplied one (URL /
 * persisted); otherwise exchange the auth token for one via the session API.
 * Never throws — a failed handshake just resolves to an empty handle.
 */
async function resolveSessionInfo(explicit: string, authToken: string): Promise<string> {
  if (explicit) {
    return explicit;
  }
  if (!authToken) {
    return '';
  }
  try {
    return await fetchSessionInfo();
  } catch {
    return '';
  }
}

/** Every URL param name we might read merchant/token/session info from. */
const AUTH_URL_PARAM_KEYS = [
  'merchant',
  'merchantDomain',
  'shop',
  'token',
  'x-auth-token',
  'authToken',
  'sessionInfo',
  'x-session-info',
];

function readFromUrl(): { merchant: string; token: string; sessionInfo: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    merchant: (params.get('merchant') || params.get('merchantDomain') || params.get('shop') || '').trim(),
    token: (params.get('token') || params.get('x-auth-token') || params.get('authToken') || '').trim(),
    sessionInfo: (params.get('sessionInfo') || params.get('x-session-info') || '').trim(),
  };
}

/**
 * Remove the host-injected auth params from the address bar so they can't
 * silently re-connect the session after an explicit logout (or on refresh).
 * Preserves any other query params and the HashRouter route.
 */
function clearAuthParamsFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of AUTH_URL_PARAM_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (changed) {
    window.history.replaceState(null, '', url.toString());
  }
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      merchantDomain: '',
      authToken: '',
      sessionInfo: '',
      authExpired: false,
      isReady: false,
      isLoggingOut: false,

      isConnected: () => {
        const { merchantDomain, authToken, authExpired } = get();
        return Boolean(merchantDomain) && Boolean(authToken) && !authExpired;
      },

      bootstrap: async () => {
        try {
          const fromUrl = readFromUrl();
          const merchantDomain = fromUrl.merchant || get().merchantDomain || environment.defaultMerchantDomain;
          const authToken = fromUrl.token || get().authToken || environment.defaultAuthToken;
          // Push creds into the request context first so the session call is authed,
          // and clear any prior expiry so a bad handshake can re-flag it below.
          setActiveMerchantDomain(merchantDomain);
          setActiveAuthToken(authToken || null);
          set({ authExpired: false });

          // Use the URL-supplied session info if present, otherwise fetch a fresh
          // one. Never fall back to the persisted value — it's stale, and reusing
          // it is exactly what made the dashboard send the previous session.
          const sessionInfo = await resolveSessionInfo(fromUrl.sessionInfo, authToken);
          setActiveSessionInfo(sessionInfo || null);
          // A 401 / "Missing session-info header" during the handshake flips
          // authExpired via the client handler — keep that flag if it fired.
          set({ merchantDomain, authToken, sessionInfo, authExpired: get().authExpired });
          // Params are now consumed (into state + sessionStorage) — drop them from
          // the address bar so a later logout / refresh can't silently re-connect.
          clearAuthParamsFromUrl();
        } finally {
          // Unblock the shell only now — the fresh session info is in the request
          // context, so the dashboard's first calls won't use the stale one.
          set({ isReady: true });
        }
      },

      connect: async (merchantDomain: string, authToken: string, sessionInfo = '') => {
        const trimmedDomain = merchantDomain.trim();
        const trimmedToken = authToken.trim();
        // Push creds into the request context first so the session call is authed,
        // and clear any prior expiry so a bad handshake can re-flag it below.
        setActiveMerchantDomain(trimmedDomain);
        setActiveAuthToken(trimmedToken || null);
        set({ authExpired: false });

        const resolvedSessionInfo = await resolveSessionInfo(sessionInfo.trim(), trimmedToken);
        setActiveSessionInfo(resolvedSessionInfo || null);
        set({
          merchantDomain: trimmedDomain,
          authToken: trimmedToken,
          sessionInfo: resolvedSessionInfo,
          authExpired: get().authExpired,
        });
      },

      disconnect: () => {
        // Drop the host-injected params first so ConnectScreen can't immediately
        // re-connect from the still-present URL after we clear the session.
        clearAuthParamsFromUrl();
        setActiveAuthToken(null);
        setActiveSessionInfo(null);
        // isLoggingOut suppresses the Connect screen while a caller (e.g. the
        // Logout button) redirects the tab elsewhere — see App.tsx.
        set({ authToken: '', sessionInfo: '', authExpired: false, isLoggingOut: true });
      },

      redirectToLogout: () => {
        get().disconnect();
        window.location.href = LOGOUT_REDIRECT_URL;
      },

      flagAuthExpired: () => set({ authExpired: true }),
    }),
    {
      name: 'fastrr-return-session',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        merchantDomain: state.merchantDomain,
        authToken: state.authToken,
        sessionInfo: state.sessionInfo,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          setActiveMerchantDomain(state.merchantDomain);
          setActiveAuthToken(state.authToken || null);
          setActiveSessionInfo(state.sessionInfo || null);
        }
      },
    },
  ),
);

// Let the API client redirect out of the app whenever a call comes back
// unauthorized / missing session info (see `isSessionInvalid` in api/client.ts).
setAuthExpiredHandler(() => useSessionStore.getState().redirectToLogout());
